import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Copy, Smartphone, QrCode, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function UpiPaymentBox() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [qrs, setQRs] = useState([]);
  const [method, setMethod] = useState("upi");
  const [txnId, setTxnId] = useState("");
  const [amount, setAmount] = useState("165");
  const [payerName, setPayerName] = useState("");
  const [payerPhone, setPayerPhone] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payment-qrs?active=true");
        setQRs(data);
      } catch {}
    })();
  }, []);

  if (!settings) return null;
  const hasUpi = !!settings.upi_id;
  const hasPhonePe = !!settings.phonepe_number;
  if (!hasUpi && !hasPhonePe && qrs.length === 0) return null;

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ನಕಲಿಸಲಾಗಿದೆ`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const upiLink = () => {
    if (!settings.upi_id) return "#";
    const payee = encodeURIComponent(settings.payee_name || "Namaste Karnataka");
    return `upi://pay?pa=${settings.upi_id}&pn=${payee}&am=${amount}&cu=INR&tn=Premium%20Subscription`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user || user === false) {
      toast.error("ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/payments/manual", {
        method,
        transaction_id: txnId.trim(),
        amount: parseFloat(amount),
        payer_name: payerName,
        payer_phone: payerPhone,
        note,
      });
      setSubmitted(true);
      toast.success("Payment submitted — admin will verify shortly");
    } catch {
      toast.error("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="border-2 border-primary bg-primary/5 p-6 rounded-sm text-center" data-testid="manual-payment-submitted">
        <CheckCircle2 className="w-12 h-12 mx-auto text-primary" />
        <h3 className="font-kannada-heading text-2xl mt-3">ಪಾವತಿ ಸಲ್ಲಿಸಲಾಗಿದೆ</h3>
        <p className="text-sm text-muted-foreground mt-2 font-kannada-body">
          ನಿಮ್ಮ ಟ್ರಾನ್ಸಾಕ್ಷನ್ ಐಡಿ <span className="font-mono font-bold">{txnId}</span> ಅನ್ನು ನಾವು ಪರಿಶೀಲಿಸುತ್ತಿದ್ದೇವೆ. ಸಾಮಾನ್ಯವಾಗಿ 24 ಗಂಟೆಗಳಲ್ಲಿ ಪ್ರೀಮಿಯಂ ಸಕ್ರಿಯಗೊಳ್ಳುತ್ತದೆ.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border bg-card p-6 rounded-sm" data-testid="upi-payment-box">
      <div className="flex items-center gap-2 mb-4">
        <Smartphone className="w-5 h-5 text-primary" />
        <h3 className="font-kannada-heading text-xl">UPI / PhonePe ಮೂಲಕ ನೇರ ಪಾವತಿ</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5 font-kannada-body">
        Stripe ಇಲ್ಲದೇ ನೇರವಾಗಿ UPI ಬಳಸಿ ಪಾವತಿಸಿ. ಪಾವತಿಸಿದ ನಂತರ transaction ID ಕೆಳಗೆ ಸಲ್ಲಿಸಿ; ಅಡ್ಮಿನ್ 24 ಗಂಟೆಗಳಲ್ಲಿ ಸಕ್ರಿಯಗೊಳಿಸುತ್ತಾರೆ.
      </p>

      {/* Payment details */}
      <div className="grid gap-4 md:grid-cols-2 mb-6">
        {hasUpi && (
          <div className="border border-border p-4 rounded-sm bg-muted/30">
            <div className="font-ui text-[10px] uppercase tracking-widest text-muted-foreground">UPI ID</div>
            <div className="flex items-center gap-2 mt-1">
              <code className="font-mono text-base flex-1 break-all">{settings.upi_id}</code>
              <button
                onClick={() => copyText(settings.upi_id, "UPI ID")}
                className="p-2 hover:bg-foreground hover:text-background rounded-sm transition-colors"
                data-testid="copy-upi-id"
                aria-label="Copy UPI ID"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <a
              href={upiLink()}
              className="inline-block mt-3 text-xs font-bold uppercase tracking-widest text-primary hover:underline"
              data-testid="upi-app-link"
            >
              Open in UPI app →
            </a>
          </div>
        )}
        {hasPhonePe && (
          <div className="border border-border p-4 rounded-sm bg-muted/30">
            <div className="font-ui text-[10px] uppercase tracking-widest text-muted-foreground">PhonePe / GPay Number</div>
            <div className="flex items-center gap-2 mt-1">
              <code className="font-mono text-base flex-1">{settings.phonepe_number}</code>
              <button
                onClick={() => copyText(settings.phonepe_number, "Phone")}
                className="p-2 hover:bg-foreground hover:text-background rounded-sm transition-colors"
                data-testid="copy-phonepe"
                aria-label="Copy phone number"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {settings.upi_qr_url && (
        <div className="mb-6 text-center">
          <div className="font-ui text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center justify-center gap-1">
            <QrCode className="w-3 h-3" /> Or scan the QR
          </div>
          <img
            src={settings.upi_qr_url}
            alt="UPI QR"
            className="w-48 h-48 mx-auto object-contain border border-border rounded-sm p-2 bg-white"
            data-testid="upi-qr"
          />
        </div>
      )}

      {qrs.length > 0 && (
        <div className="mb-6">
          <div className="font-ui text-[10px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1">
            <QrCode className="w-3 h-3" /> Scan any QR to pay
          </div>
          <div className={`grid gap-4 ${qrs.length === 1 ? "grid-cols-1 max-w-xs mx-auto" : "sm:grid-cols-2 lg:grid-cols-3"}`} data-testid="payment-qr-grid">
            {qrs.map((q) => (
              <div key={q.id} className="border border-border rounded-sm p-3 bg-white" data-testid={`payment-qr-${q.id}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-primary text-center mb-2">
                  {q.method}
                </div>
                <img src={q.image_url} alt={q.title} className="w-full aspect-square object-contain" />
                <div className="text-xs text-center text-muted-foreground mt-2 font-semibold line-clamp-1" title={q.title}>
                  {q.title}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {user && user !== false ? (
        <form onSubmit={submit} className="space-y-4 border-t border-border pt-6">
          <div className="font-ui text-xs uppercase tracking-widest text-muted-foreground">
            After paying, submit your transaction details
          </div>
          <RadioGroup value={method} onValueChange={setMethod} className="flex gap-4">
            {hasUpi && (
              <label className="flex items-center gap-2 cursor-pointer" data-testid="method-upi">
                <RadioGroupItem value="upi" />
                <span className="text-sm font-semibold">UPI</span>
              </label>
            )}
            {hasPhonePe && (
              <label className="flex items-center gap-2 cursor-pointer" data-testid="method-phonepe">
                <RadioGroupItem value="phonepe" />
                <span className="text-sm font-semibold">PhonePe / GPay</span>
              </label>
            )}
            <label className="flex items-center gap-2 cursor-pointer" data-testid="method-bank">
              <RadioGroupItem value="bank" />
              <span className="text-sm font-semibold">Bank</span>
            </label>
          </RadioGroup>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-widest">Transaction / UTR ID *</Label>
              <Input required value={txnId} onChange={(e) => setTxnId(e.target.value)} className="rounded-none mt-1" placeholder="e.g. 314159265358" data-testid="txn-id" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Amount Paid (₹) *</Label>
              <Input required type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-none mt-1" data-testid="txn-amount" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Your Name</Label>
              <Input value={payerName} onChange={(e) => setPayerName(e.target.value)} className="rounded-none mt-1" data-testid="payer-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Your Phone (optional)</Label>
              <Input value={payerPhone} onChange={(e) => setPayerPhone(e.target.value)} className="rounded-none mt-1" data-testid="payer-phone" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none mt-1" rows={2} data-testid="txn-note" />
          </div>
          <Button type="submit" disabled={submitting} className="rounded-none" data-testid="submit-manual-payment">
            {submitting ? "Submitting..." : "Submit for Verification"}
          </Button>
        </form>
      ) : (
        <p className="text-sm text-muted-foreground border-t border-border pt-6">
          Submit transaction after paying —{" "}
          <Link to="/login" className="text-primary underline underline-offset-4 font-semibold">login</Link> to proceed.
        </p>
      )}
    </div>
  );
}
